import Dexie, { type Table } from 'dexie';

export interface ReadingHistoryEntry {
  id?: number;
  verseId: string;
  timestamp: Date;
  duration: number;
}

export interface StudyNote {
  id?: number;
  verseId: string;
  content: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface Favorite {
  id?: number;
  verseId: string;
  createdAt: Date;
}

export interface Highlight {
  id?: number;
  verseId: string;
  color: string;
  createdAt: Date;
}

export interface VerseAnalysisCache {
  id?: string;
  analysis: any;
  timestamp: string;
  expiresAt: Date;
  syncedWithServer: boolean;
}

export interface CrossReferenceCache {
  id: string;
  traditional: any[];
  semantic: any[];
  timestamp: string;
}

export interface StudyGroup {
  id?: number;
  name: string;
  description: string;
  createdBy: string;
  createdAt: Date;
  members: string[];
  isPublic: boolean;
}

export interface StudySession {
  id?: number;
  groupId: number;
  name: string;
  description: string;
  scheduledFor: Date;
  duration: number;
  verses: string[];
  notes: string;
  participants: string[];
  createdBy: string;
  createdAt: Date;
}

export interface SharedNote {
  id: string;
  groupId: number;
  userId: string;
  content: string;
  sharedBy: string;
  sharedAt: Date;
  createdAt: Date;
  reactions: { [userId: string]: string };
  comments: {
    id: string;
    userId: string;
    content: string;
    createdAt: Date;
  }[];
}

class BibleDB extends Dexie {
  readingHistory!: Table<ReadingHistoryEntry>;
  notes!: Table<StudyNote>;
  favorites!: Table<Favorite>;
  highlights!: Table<Highlight>;
  analysisCache!: Table<VerseAnalysisCache>;
  crossReferenceCache!: Table<CrossReferenceCache>;
  studyGroups!: Table<StudyGroup>;
  studySessions!: Table<StudySession>;
  sharedNotes!: Table<SharedNote>;

  private initialized: boolean = false;

  constructor() {
    super('BibleDB');
    
    this.version(1).stores({
      readingHistory: '++id, verseId, timestamp',
      notes: '++id, verseId, tags, createdAt',
      favorites: '++id, verseId, createdAt',
      highlights: '++id, verseId, color'
    });

    this.version(2).stores({
      readingHistory: '++id, verseId, timestamp',
      notes: '++id, verseId, tags, createdAt',
      favorites: '++id, verseId, createdAt',
      highlights: '++id, verseId, color',
      analysisCache: 'id, timestamp, expiresAt, syncedWithServer'
    });

    this.version(3).stores({
      readingHistory: '++id, verseId, timestamp',
      notes: '++id, verseId, tags, createdAt',
      favorites: '++id, verseId, createdAt',
      highlights: '++id, verseId, color',
      analysisCache: 'id, timestamp, expiresAt, syncedWithServer',
      crossReferenceCache: 'id, timestamp'
    });

    this.version(4).stores({
      readingHistory: '++id, verseId, timestamp',
      notes: '++id, verseId, tags, createdAt',
      favorites: '++id, verseId, createdAt',
      highlights: '++id, verseId, color',
      analysisCache: 'id, timestamp, expiresAt, syncedWithServer',
      crossReferenceCache: 'id, timestamp',
      studyGroups: '++id, name, createdBy, createdAt, isPublic',
      studySessions: '++id, groupId, scheduledFor, createdBy, createdAt',
      sharedNotes: '++id, groupId, verseId, sharedBy, sharedAt'
    });
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    await this.open();
    this.initialized = true;
  }

  async isInitialized(): Promise<boolean> {
    return this.initialized;
  }

  async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  // Analysis cache methods
  async cacheAnalysis(verseReference: string, data: {
    analysis: any;
    timestamp: string;
    expiresAt: Date;
    syncedWithServer: boolean;
  }): Promise<void> {
    await this.ensureInitialized();
    await this.analysisCache.put({
      id: verseReference,
      ...data
    });
  }

  async getCachedAnalysis(verseReference: string): Promise<VerseAnalysisCache | undefined> {
    await this.ensureInitialized();
    const cached = await this.analysisCache.get(verseReference);
    if (!cached) return undefined;

    if (new Date() > cached.expiresAt) {
      await this.analysisCache.delete(verseReference);
      return undefined;
    }

    return cached;
  }

  async cleanExpiredCache(): Promise<void> {
    await this.ensureInitialized();
    const now = new Date();
    await this.analysisCache
      .where('expiresAt')
      .below(now)
      .delete();
  }

  // Cross-reference caching
  async getCachedCrossReferences(verseReference: string): Promise<CrossReferenceCache | undefined> {
    await this.ensureInitialized();
    const cached = await this.crossReferenceCache.get(verseReference);
    if (!cached || this.isStale(cached.timestamp)) {
      return undefined;
    }
    return cached;
  }

  async cacheCrossReferences(verseReference: string, data: {
    traditional: any[];
    semantic: any[];
    timestamp: string;
  }): Promise<void> {
    await this.ensureInitialized();
    await this.crossReferenceCache.put({
      id: verseReference,
      ...data
    });
  }

  private isStale(timestamp: string): boolean {
    const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
    const timestampDate = new Date(timestamp);
    return Date.now() - timestampDate.getTime() > CACHE_TTL;
  }

  // Background sync methods
  async getUnsyncedAnalyses(): Promise<VerseAnalysisCache[]> {
    await this.ensureInitialized();
    return await this.analysisCache
      .where('syncedWithServer')
      .equals(false)
      .toArray();
  }

  async markAnalysisSynced(id: string): Promise<void> {
    await this.ensureInitialized();
    await this.analysisCache.update(id, { syncedWithServer: true });
  }
}

// Create and initialize the database instance
export const db = new BibleDB();

// Initialize the database when the module loads
db.initialize().catch(console.error);